import {
  URL_REGISTER_ADVERTISER_COMPLETE,
  URL_ROOT,
} from '@seek/adv-constants';
import { useTrackView } from '@seek/cmsu-analytics';
import { EMPLOYER_CONTACT_US_URL } from '@seek/shared-constants';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Heading,
  Strong,
  List,
  Card,
  Stack,
  TextLink,
  Box,
} from 'braid-design-system';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { META_TITLE_REGISTRATION_COMPLETE } from 'src/constants/pageMetaData';
import { REGISTER_COMPLETE_PAGE_VIEW } from 'src/constants/pageViewUniqueKeys';
import { useConfig } from 'src/hooks/context';
import { useHygraphRichText } from 'src/hooks/use-hygraph-rich-text';
import { CREATE_ADVERTISER_COMPLETE_CONTACT_US } from 'src/modules/ApiClient/cms/richTextConstants';
import CsPhoneNumber from 'src/views/shared/CsPhoneNumber/CsPhoneNumber';
import HygraphRichText from 'src/views/shared/Hygraph/HygraphRichText';
import PageHeading from 'src/views/shared/PageHeading/PageHeading';
import Panel from 'src/views/shared/Panel/Panel';
import SeoMeta from 'src/views/shared/SeoMeta/SeoMeta';

import translations from './.vocab';

const RegistrationCompletePage = () => {
  const { site, isAnz, absoluteEmployerUrlResolver, urlResolver } = useConfig();
  const location = useLocation();
  const { t } = useTranslations(translations);
  const registrationCompleteContactUsContent = useHygraphRichText(
    CREATE_ADVERTISER_COMPLETE_CONTACT_US,
  );

  const fullUrl = absoluteEmployerUrlResolver(URL_REGISTER_ADVERTISER_COMPLETE);

  const trackFunction = useTrackView(REGISTER_COMPLETE_PAGE_VIEW);

  useEffect(() => {
    if (trackFunction) {
      trackFunction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!location.state) {
    return <Navigate to={URL_ROOT} />;
  }

  const { name = '', email } = location.state.registeredUserDetails;

  const AnzContactDetails = () => (
    <>
      {t('ANZ Contact customer service message')} <CsPhoneNumber site={site} />
    </>
  );

  const NonAnzContactDetails = () => (
    <>
      {t('Contact customer service message', {
        Link: (children) => (
          <TextLink
            href={urlResolver({
              path: EMPLOYER_CONTACT_US_URL[site],
            })}
            target="_blank"
          >
            {children}
          </TextLink>
        ),
      })}
    </>
  );

  return (
    <>
      <SeoMeta
        title={META_TITLE_REGISTRATION_COMPLETE}
        description={''}
        url={fullUrl}
      />
      <Box paddingX={'gutter'}>
        <PageHeading title={t('Registration complete page title', { name })} />

        <Card>
          <Stack space="medium">
            <Text component="p" data={{ 'dd-privacy': 'mask' }}>
              {t('Sent activation email link message')} <Strong>{email}</Strong>
              .
            </Text>
            <Text component="p">
              {t('Follow email link instruction message')}
            </Text>
          </Stack>
        </Card>

        <Panel>
          <Stack space="medium">
            <Heading level="3">{t('Did not receive the email title')}</Heading>
            <List>
              <Text>{t('Check your spam folder')}</Text>
              <Text>{t('Check email is correct message')}</Text>
              {registrationCompleteContactUsContent ? (
                <HygraphRichText
                  textProps={{ component: 'p' }}
                  richTextData={registrationCompleteContactUsContent}
                />
              ) : (
                <Text>
                  {isAnz ? <AnzContactDetails /> : <NonAnzContactDetails />}
                </Text>
              )}
            </List>
          </Stack>
        </Panel>
      </Box>
    </>
  );
};

export default RegistrationCompletePage;
